'use client';

import 'lib/yup-config';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { Footer, Heading } from 'components/atomic/atoms';
import { PhoneCodes } from 'components/atomic/nuclei';
import { HeaderOrganism } from 'components/atomic/organism';
import { NAME_VALIDATION_CONFIG, staticMediaStoreBaseURL } from 'config/common';
import baseConfig from 'config/index';
import phoneConfig from 'config/phone';
import phoneCountryCodes from 'config/phoneCountryCodes';
import {
  captureGTMEventCTAClick,
  GTM_EVENTS_CTA_NAME,
  PAGE_NAME
} from 'lib/gtm';
import mobileNumber from 'lib/validations/mobile-number';
import { yupValidator } from 'lib/yup-validator';
import * as ContactUsService from 'services/contactUs.service';

const { isMobileNumberValid } = mobileNumber;

const {
  firstCountryToShow,
  mobileBasicValidationFailureError,
  mobileInputError,
  mobileNotStartZeroRegex,
  phoneNumbers
} = phoneConfig;

const buildingIcon = '/icons/building.svg';
const addressIcon = '/icons/address.svg';
const phoneCallIcon = '/icons/phone-call.svg';
const emailIcon = '/icons/email.svg';

const ContactUs = () => {
  const contactUsTranslations = useTranslations('contactus');
  const direction = useState('ltr');

  const schema = yup.object().shape({
    fullName: yup
      .string()
      .required('Full name is mandatory!')
      .matches(
        NAME_VALIDATION_CONFIG.nameRegex,
        NAME_VALIDATION_CONFIG.nameInputError
      ),
    companyName: yup
      .string()
      .test(
        'is-valid-company-name',
        `${contactUsTranslations('formCompanyName')} ${
          NAME_VALIDATION_CONFIG.nameInputError
        }`,
        (value) => {
          if (!value) {
            return true;
          }
          return NAME_VALIDATION_CONFIG.nameRegex.test(value);
        }
      ),
    email: yup
      .string()
      .trim()
      .transform((val) => (val ? val.toLowerCase() : ''))
      .email('Invalid email format')
      .isIcannEmail('Invalid email domain or TLD')
      .required('Contact Email is required'),
    phoneNumber: yup
      .string()
      .required(mobileBasicValidationFailureError)
      .matches(mobileNotStartZeroRegex, mobileBasicValidationFailureError)
      .test('is-valid', mobileInputError, function (val) {
        const code = this.parent.countryCode || '';
        return isMobileNumberValid(`${code}${val}`);
      }),
    message: yup.string(),
    countryCode: yup.string().required()
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupValidator(schema),
    defaultValues: {
      fullName: '',
      companyName: '',
      email: '',
      countryCode: phoneCountryCodes.AE.code,
      phoneNumber: '',
      message: ''
    }
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const submitClick = (data) => {
    ContactUsService.contactUs(data).then((response) => {
      if (response.status) {
        captureGTMEventCTAClick({
          ctaName: GTM_EVENTS_CTA_NAME.CONTACT_US,
          pageName: PAGE_NAME.CONTACT_US.label
        });
        setIsSubmitted(true);
        reset();
      }
    });
  };

  const ShowErrorMessage = ({ errorMessage }) => (
    <div className='font-light text-xs text-red-700'>{errorMessage}</div>
  );

  return (
    <div dir={direction}>
      <HeaderOrganism heading='Contact Us' />
      <div className='text-xl md:text-3xl mt-5 ml-5 font-bold md:mt-10 md:ml-15'>
        {'header'}
      </div>
      <div className='md:flex md:mt-5 md:ml-10 md:mr-10 md:rounded-2xl'>
        <div className='md:flex-initial h-full md:mr-20 mt-5 md:mt-0 pb-5 md:pb-15 ml-5 mr-5 bg-lightpink sm:rounded-2xl'>
          <Heading
            level={3}
            className='font-semibold text-xl md:pl-10 pl-5 pt-5 font-Montserrat'
          >
            {contactUsTranslations('subHeader')}
          </Heading>
          <div className='flex items-start md:ms-10 md:pt-10 ms-5 pt-5'>
            <div className='relative h-5 w-5 min-w-6'>
              <Image
                alt='buildingIcon'
                src={`${staticMediaStoreBaseURL}${buildingIcon}`}
                fill
                sizes='100vw'
                style={{ objectFit: 'cover' }}
              />
            </div>
            <div className='ms-2'>
              <span className='font-normal text-sm'>
                {contactUsTranslations('companyName')}
              </span>
              <p className='md:w-2/5 mt-1 pe-5 text-light-gray font-light text-xs md:text-sm'>
                {' '}
                {contactUsTranslations('aboutCompany')}
              </p>
            </div>
          </div>
          <div className='flex items-start md:ms-10 md:pt-10 ms-5 pt-5'>
            <div className='relative h-5 w-5 min-w-6'>
              <Image
                alt='addressIcon'
                src={`${staticMediaStoreBaseURL}${addressIcon}`}
                fill
                sizes='100vw'
                style={{ objectFit: 'cover' }}
              />
            </div>
            <div className='ms-2'>
              <span className='font-normal text-sm'>
                {contactUsTranslations('address')}
              </span>
              <p className='md:w-2/5 mt-1 pe-5 text-light-gray font-light text-xs md:text-sm'>
                {' '}
                {contactUsTranslations('addressDescription')}
              </p>
            </div>
          </div>
          <div className='flex items-start md:ms-10 md:pt-10 ms-5 pt-5'>
            <div className='relative h-5 w-5 min-w-6'>
              <Image
                alt='phoneCallIcon'
                src={`${staticMediaStoreBaseURL}${phoneCallIcon}`}
                fill
                sizes='100vw'
                style={{ objectFit: 'cover' }}
              />
            </div>
            <div className='ms-2'>
              <span className='font-normal text-sm'>
                {contactUsTranslations('phoneText')}
              </span>
              <p className='mt-1 pe-5'>
                <a
                  className='font-normal text-light-gray text-xs md:text-sm'
                  href={`tel:${phoneNumbers.haflaLandlineMaqsam}`}
                >
                  {phoneNumbers.haflaLandlineMaqsam}
                </a>
              </p>
            </div>
          </div>
          <div className='flex items-start md:ms-10 md:pt-10 ms-5 pt-5'>
            <div className='relative h-5 w-5 min-w-6'>
              <Image
                alt='email'
                src={`${staticMediaStoreBaseURL}${emailIcon}`}
                fill
                sizes='100vw'
                style={{ objectFit: 'cover' }}
              />
            </div>
            <div className='ms-2'>
              <span className='font-normal text-sm'>
                {contactUsTranslations('emailText')}
              </span>
              <p className='mt-1 pe-5'>
                <a
                  className='font-light text-light-gray text-xs md:text-sm'
                  href={`mailto:${baseConfig.haflaEmail.contactUs}`}
                >
                  {baseConfig.haflaEmail.contactUs}
                </a>
              </p>
            </div>
          </div>
        </div>
        <div className='md:ml-0 ml-5 mr-5 rounded-2xl'>
          <Heading
            level={3}
            className='font-semibold text-coral text-xl md:pl-5 pt-5 font-Montserrat'
          >
            {contactUsTranslations('helpText')}
          </Heading>

          <div className='md:ml-5 mt-2'>
            <p className='md:w-9/10 text-left text-light-grey font-light md:text-normal text-sm'>
              {contactUsTranslations('helpDescription')}
            </p>
          </div>
          <div className='md:ml-5 mt-5 md:mt-6'>
            <form onSubmit={handleSubmit(submitClick)}>
              <div className='mb-5'>
                <div className='font-light text-sm'>
                  {contactUsTranslations('formFullName')}*
                </div>
                <div>
                  <input
                    className='border-b border-gray-150 outline-none w-full text-sm font-normal h-8 md:text-normal md:font-normal'
                    type='text'
                    placeholder={contactUsTranslations(
                      'formFullNamePlaceHolder'
                    )}
                    {...register('fullName')}
                  />
                  {errors.fullName && (
                    <ShowErrorMessage errorMessage={errors.fullName.message} />
                  )}
                </div>
              </div>
              <div className='mb-5 border-b border-gray-150'>
                <div className='font-light text-sm'>
                  {contactUsTranslations('formCompanyName')}
                </div>
                <div>
                  <input
                    className='outline-none w-full text-sm font-normal h-8 md:text-normal md:font-normal'
                    type='text'
                    placeholder={contactUsTranslations(
                      'formCompanyNamePlaceHolder'
                    )}
                    {...register('companyName')}
                  />
                  {errors.companyName && (
                    <ShowErrorMessage
                      errorMessage={errors.companyName.message}
                    />
                  )}
                </div>
              </div>
              <div className='mb-5'>
                <div className='font-light text-sm'>
                  {contactUsTranslations('formEmail')}*
                </div>
                <div>
                  <input
                    className='border-b border-gray-150 outline-none w-full text-sm font-normal h-8 md:text-normal md:font-normal'
                    {...register('email')}
                    placeholder={contactUsTranslations('formEmailPlaceHolder')}
                  />
                </div>
                {errors.email && (
                  <ShowErrorMessage errorMessage={errors.email.message} />
                )}
              </div>
              <div className='mb-5'>
                <div className='font-light text-sm'>
                  {contactUsTranslations('formPhone')}
                </div>
                <div>
                  <div className='flex flex-row items-center'>
                    <PhoneCodes
                      onSelect={(code) => setValue('countryCode', code)}
                      codeFirst
                      showFirst={firstCountryToShow}
                      value={watch('countryCode')}
                      id='countryCode'
                      name='countryCode'
                      className='ml-1 outline-none md:w-14 w-8 border-b border-gray-150
                    text-sm font-normal h-8 md:text-normal md:font-normal'
                    />
                    <div className='w-full me-5'>
                      {' '}
                      <input
                        className='outline-none ms-4 w-full border-b border-gray-150 text-sm font-normal h-8 md:text-normal md:font-normal'
                        type='tel'
                        placeholder={contactUsTranslations(
                          'formPhonePlaceHolder'
                        )}
                        {...register('phoneNumber')}
                      />
                      {errors.phoneNumber && (
                        <ShowErrorMessage
                          errorMessage={errors.phoneNumber.message}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className='mb-5 border-b border-gray-150'>
                <div className='font-light text-sm'>
                  {contactUsTranslations('formMessage')}
                </div>
                <div>
                  <input
                    className='outline-none w-full text-sm font-normal h-8 md:text-normal md:font-medium'
                    type='text'
                    placeholder={contactUsTranslations(
                      'formMessagePlaceHolder'
                    )}
                    {...register('message')}
                  />
                  {errors.message && (
                    <ShowErrorMessage errorMessage={errors.message.message} />
                  )}
                </div>
              </div>
              {!isSubmitted ? (
                <button
                  type='submit'
                  className='bg-coral text-white text-center p-3 w-full rounded-lg'
                >
                  {contactUsTranslations('formButton')}
                </button>
              ) : (
                <div className='font-bold text-sm text-green-500'>
                  {contactUsTranslations('successMessage')}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      <div className='mt-11 md:mt-20'>
        <Footer />
      </div>
    </div>
  );
};

export default ContactUs;
